import React from 'react';
import {
  CheckCircle,
  Info,
  Lightbulb,
  ChevronRight,
  Shield,
  Lock,
  Headphones,
  Smartphone,
} from 'lucide-react';


// Reusable components from ManagingDashboard
const ExampleBox = ({ children }) => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r-lg">
    <div className="flex items-start">
      <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-blue-800 mb-1">Example</p>
        <div className="text-blue-700 text-sm">{children}</div>
      </div>
    </div>
  </div>
);

const ProTip = ({ children }) => (
  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
    <div className="flex items-start">
      <Lightbulb className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-yellow-800 mb-1">Pro Tip</p>
        <div className="text-yellow-700 text-sm">{children}</div>
      </div>
    </div>
  </div>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f09433" />
        <stop offset="25%" stopColor="#e6683c" />
        <stop offset="50%" stopColor="#dc2743" />
        <stop offset="75%" stopColor="#cc2366" />
        <stop offset="100%" stopColor="#bc1888" />
      </linearGradient>
    </defs>
    <path d="M16.98 0a6.9 6.9 0 0 1 6.9 6.9v10.2a6.9 6.9 0 0 1-6.9 6.9H7.02a6.9 6.9 0 0 1-6.9-6.9V6.9A6.9 6.9 0 0 1 7.02 0h9.96zM12 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488" />
  </svg>
);

const Security = () => {
  return (
    <div className="min-h-screen bg-gray-50 mt-[32px]">
      <div className="">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="bg-blue-100 rounded-lg p-2 mr-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Security: Protecting Your Data and Business
            </h1>
          </div>
          <p className="text-gray-600 mb-6">
            At ChatInject, your security is our top priority. We’ve implemented robust measures to safeguard your business data and personal information across all connected platforms, ensuring you can focus on growth with peace of mind.
          </p>
          <div className="flex space-x-2 mb-6">
            <FacebookIcon />
            <InstagramIcon />
            <WhatsAppIcon />
          </div>

          {/* Two-Factor Authentication */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="w-5 h-5 mr-2 text-blue-600" />
              Two-Factor Authentication (2FA): Your First Line of Defense
            </h2>
            <p className="text-gray-600 mb-4">
              Two-Factor Authentication (2FA) adds an extra layer of protection by requiring two forms of verification.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <p className="text-gray-700 text-sm">Your password</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <p className="text-gray-700 text-sm">A one-time code sent to your mobile device or email</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Why Enable 2FA?</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Prevent unauthorized access even if someone has your password.',
                    'Protect sensitive information, such as chat history and customer data.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <ExampleBox>
                  If someone tries to log in with your password, they’ll still need the 2FA code sent to your phone, keeping your account secure.
                </ExampleBox>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Enable 2FA</h3>
                <ol className="space-y-3 text-gray-600">
                  {[
                    'Go to <strong>Settings → Privacy Settings</strong>.',
                    'Toggle the <strong>Two-Factor Authentication</strong> option to "On".',
                    'Choose your preferred method:',
                    'Enter the verification code to activate 2FA.',
                  ].map((step, index) => (
                    <li key={index} className="flex items-start">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: step }} />
                    </li>
                  ))}
                </ol>
                <div className="ml-6 space-y-3 mt-3">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-1">SMS Verification</h4>
                    <p className="text-gray-600 text-sm">Receive a one-time code via text message.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-1">Authenticator App</h4>
                    <p className="text-gray-600 text-sm">Use apps like Google Authenticator or Authy for added security.</p>
                  </div>
                </div>
                <ProTip>
                  Use an authenticator app for 2FA instead of SMS for faster and more secure code delivery.
                </ProTip>
              </div>
            </div>
          </section>

          {/* Data Encryption */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-purple-600" />
              Data Encryption: Keeping Your Information Private
            </h2>
            <p className="text-gray-600 mb-4">
              All data transferred between your devices and ChatInject is protected with end-to-end encryption, ensuring only you and the intended recipient can access the information.
            </p>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Encryption in Action</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-gray-900 mb-2">Messages</h4>
                    <p className="text-gray-600 text-sm">All messages sent and received through ChatInject are encrypted.</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-gray-900 mb-2">Platform Connections</h4>
                    <p className="text-gray-600 text-sm">Connections to platforms like Instagram, WhatsApp, Facebook, and Telegram are made via secure APIs using encrypted tokens.</p>
                  </div>
                </div>
                <ExampleBox>
                  Your customer messages on WhatsApp are encrypted, ensuring no third party can read them during transmission.
                </ExampleBox>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What This Means for You</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Your conversations cannot be intercepted by third parties.',
                    'Sensitive information like customer profiles and automated responses is protected.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-purple-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Customer Support for Security */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Headphones className="w-5 h-5 mr-2 text-orange-600" />
              Customer Support for Security Concerns
            </h2>
            <p className="text-gray-600 mb-4">
              Our dedicated support team is ready to assist with any security-related issues.
            </p>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What We Offer</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Help with 2FA setup or recovery.',
                    'Assistance in securing your account after suspicious activity.',
                    'Guidance on managing privacy and security settings.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-orange-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Reach Support</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Visit the <strong>Help Center</strong> via the platform.',
                    'Submit a ticket or chat with our security support team for instant assistance.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-yellow-500" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                <ProTip>
                  If you suspect unauthorized access, immediately contact support and enable 2FA to secure your account.
                </ProTip>
              </div>
            </div>
          </section>

          {/* Why Security Matters */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Why Security Matters to Your Business</h2>
            <div className="bg-green-50 rounded-lg p-6">
              <p className="text-gray-600 mb-4">
                Your social media accounts are essential for connecting with customers, and protecting them is vital for your business’s reputation and trust. ChatInject provides enterprise-grade security features tailored for businesses of all sizes.
              </p>
              <p className="text-gray-600">
                By leveraging our security tools, you can focus on growing your business, knowing your data is in safe hands.
              </p>
              <ExampleBox>
                Enable 2FA and encryption to ensure customer data shared via Instagram DMs remains private and secure.
              </ExampleBox>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-white rounded-lg shadow-sm p-6 text-center">
            <p className="text-gray-600 mb-4">
              Need help with security settings? Contact us at{' '}
              <a href="mailto:chatinject@gmail.com" className="text-blue-600 hover:text-blue-800">
                chatinject@gmail.com
              </a>{' '}
              or via the{' '}
              <a href="#" className="text-blue-600 hover:text-blue-800">
                Help Center
              </a>.
            </p>
            <div className="flex justify-center space-x-4 text-sm">
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Support Center</a>
              <span className="text-gray-300">|</span>
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Video Tutorials</a>
              <span className="text-gray-300">|</span>
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Contact Us</a>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Security;